"use client";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  getDay,
  parseISO,
} from "date-fns";
import clsx from "clsx";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CalendarDayModal from "./CalendarDayModal";
import { REST_API_URL } from "../api/config";
import { buildHeaders } from "../api/buildHeaders";
import { getCurrencySymbol } from "../utils/currency/getCurrencySymbol";

const API_BASE_URL = REST_API_URL;

export default function MonthView({
  token,
  currentDate,
  selectedDate,
  direction,
  setSelectedDate,
  setDayEvents,
  refreshKey,
  onSummaryChange,
  userCurrency,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [monthEvents, setMonthEvents] = useState({});
  const [monthSummary, setMonthSummary] = useState({});
  const [userStatus, setUserStatus] = useState(null);
  const [userCurrencyCode, setUserCurrencyCode] = useState(
    userCurrency || "USD",
  );

  const [showModal, setShowModal] = useState(false);
  const [dayAppointments, setDayAppointments] = useState([]);
  const [clickedDate, setClickedDate] = useState(null);
  const [googleEventDates, setGoogleEventDates] = useState(new Set());

  const createApiInstance = () => {
    return axios.create({
      baseURL: API_BASE_URL,
      headers: buildHeaders(),
    });
  };

  const fetchUserInfo = async () => {
    try {
      if (!token) return;

      const cachedData = localStorage.getItem("userInfo");
      if (cachedData) {
        const parsedData = JSON.parse(cachedData);
        const cacheTime = localStorage.getItem("userInfoTimestamp");
        const currentTime = new Date().getTime();
        if (cacheTime && currentTime - parseInt(cacheTime) < 3600000) {
          setUserCurrencyCode(parsedData.currency || "USD");
          setUserStatus(
            parsedData?.user?.status || parsedData?.user_status || "individual",
          );
          return parsedData;
        }
      }

      const headers = buildHeaders();

      const response = await fetch(`${API_BASE_URL}/auth/userinfo/`, {
        headers,
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const userData = await response.json();
      const status =
        userData?.user?.status || userData?.user_status || "individual";
      const currency = userData?.currency || "USD";

      setUserStatus(status);
      setUserCurrencyCode(currency);

      localStorage.setItem("userInfo", JSON.stringify(userData));
      localStorage.setItem(
        "userInfoTimestamp",
        new Date().getTime().toString(),
      );

      return userData;
    } catch (err) {
      console.error("⚠️ Failed to fetch userInfo:", err.message);
      setUserStatus("individual");
      setUserCurrencyCode("USD");
    }
  };

  const fetchMonthEvents = async () => {
    const api = createApiInstance();
    try {
      const monthStr = format(currentDate, "yyyy-MM");
      const res = await api.post(
        `${API_BASE_URL}/client/appointments-month-dates/`,
        {
          month: monthStr,
        },
      );

      const mapped = {};

      if (Array.isArray(res.data?.dates)) {
        res.data.dates.forEach((item) => {
          let dateStr = item.date || item;
          let dateKey;
          try {
            dateKey = format(parseISO(dateStr), "yyyy-MM-dd");
          } catch {
            dateKey = format(new Date(dateStr), "yyyy-MM-dd");
          }

          const appointments = item.appointments || [];
          const avatars = appointments.slice(0, 1).map((a) => a.client_avatar);

          const extra =
            appointments.length > 1 ? `+${appointments.length - 1}` : null;

          const firstAppointment = appointments[0];
          const timeRange = firstAppointment
            ? `${firstAppointment.start_time?.slice(0, 5) || "08:00"}-${
                firstAppointment.end_time?.slice(0, 5) || "09:00"
              }`
            : null;

          mapped[dateKey] = { avatars, label: timeRange, extra };
        });
      }

      if (userStatus === "individual" && Array.isArray(res.data)) {
        res.data.forEach((a) => {
          const dateKey =
            a.date ||
            a.appointment_date_time?.slice(0, 10) ||
            format(new Date(), "yyyy-MM-dd");
          mapped[dateKey] = {
            avatars: [a.client_avatar],
            label: `${a.start_time?.slice(0, 5) || "08:00"}-${
              a.end_time?.slice(0, 5) || "09:00"
            }`,
          };
        });
      }

      const monthCount = res.data?.month_count || 0;
      const monthPrice = res.data?.month_price || 0;
      const apiCurrency = res.data?.currency || userCurrencyCode;

      const currencyToUse = apiCurrency || userCurrencyCode;
      const currencySymbol = getCurrencySymbol(currencyToUse);

      setMonthSummary({
        count: monthCount,
        price: monthPrice,
        currency: currencyToUse,
        currencySymbol: currencySymbol,
      });

      if (onSummaryChange) {
        onSummaryChange({
          price: monthPrice,
          currency: currencyToUse,
          currencySymbol: currencySymbol,
        });
      }

      // Google Calendar даты — зелёные точки
      if (Array.isArray(res.data?.google_events_dates)) {
        const googleDates = new Set(
          res.data.google_events_dates.map((d) => {
            try {
              return format(parseISO(d), "yyyy-MM-dd");
            } catch {
              return format(new Date(d), "yyyy-MM-dd");
            }
          }),
        );
        setGoogleEventDates(googleDates);
      } else {
        setGoogleEventDates(new Set());
      }

      setMonthEvents(mapped);
    } catch (err) {
      console.error("❌ Month Fetch Error:", err.response?.data || err.message);
      if (err.response?.status === 401) {
        localStorage.clear();
        sessionStorage.clear();
        navigate("/sign-in");
      }
    }
  };

  const fetchDayEvents = async (day) => {
    const api = createApiInstance();
    try {
      const dateStr = format(day, "yyyy-MM-dd");
      const res = await api.post(
        `${API_BASE_URL}/client/appointments/by-date/`,
        {
          date: dateStr,
        },
      );

      const data = Array.isArray(res.data?.appointments)
        ? res.data.appointments
        : Array.isArray(res.data)
          ? res.data
          : [];

      const validItems = data.filter(
        (item) => item && (item.id || item.client_name || item.worker),
      );

      const detailedEvents = await Promise.all(
        validItems.map(async (item) => {
          try {
            if (item.status === "breake_time") return null;

            if (!item.id) {
              return {
                id: `temp_${Date.now()}_${Math.random()}`,
                name: t("home.breakTimeName"),
                service: t("home.breakTimeService"),
                master:
                  userStatus === "individual"
                    ? t("home.fallbackMasterYou")
                    : t("home.fallbackMasterGeneric"),
                comment: t("home.breakTimeComment"),
                price: 0,
                date: item.date || dateStr,
                timeStart: item.start_time?.slice(0, 5) || "00:00",
                timeEnd: item.end_time?.slice(0, 5) || "00:00",
                avatar:
                  "https://cdn-icons-png.flaticon.com/512/3050/3050525.png",
                worker_avatar: "",
                status: "Break",
                isBreak: true,
                currency: userCurrencyCode,
                currencySymbol: getCurrencySymbol(userCurrencyCode),
              };
            }

            const detailsRes = await api.get(
              `${API_BASE_URL}/client/appointment-details/`,
              {
                params: { id: item.id },
              },
            );
            const details = detailsRes.data || {};

            const masterName =
              item.worker?.[0]?.full_name ||
              details.master_name ||
              details.worker_name ||
              (userStatus === "individual"
                ? t("home.fallbackMasterYou")
                : t("home.fallbackMasterGeneric"));

            const appointmentCurrency =
              item.currency || details.currency || userCurrencyCode;
            const appointmentCurrencySymbol =
              getCurrencySymbol(appointmentCurrency);

            return {
              id: item.id,
              name:
                item.client_name ||
                details.client_name ||
                t("home.fallbackClientName"),
              service:
                item.service_name ||
                details.service_name ||
                t("home.fallbackServiceName"),
              service_id: item.service_id || details.service_id || null,
              master: masterName,
              comment:
                item.service_description ||
                details.comment ||
                t("home.fallbackNoComment"),
              price: parseFloat(item.price || details.price || 0),
              servicePrice: parseFloat(item.service_price || details.service_price || 0),
              serviceId: item.service_id || details.service_id || null,
              date:
                item.date ||
                details.date ||
                item.appointment_date_time?.slice(0, 10),
              timeStart:
                item.start_time?.slice(0, 5) ||
                details.appointment_date_time?.slice(11, 16) ||
                "08:00",
              timeEnd:
                item.end_time?.slice(0, 5) ||
                details.appointment_end_time?.slice(11, 16) ||
                "09:00",
              avatar: item.image || details.client_avatar || item.client_avatar,
              worker_avatar:
                item.worker?.[0]?.image_url || details.worker_avatar,
              status: item.status || details.status,
              currency: appointmentCurrency,
              currencySymbol: appointmentCurrencySymbol,
              // ── added_services ──
              added_services: Array.isArray(details.added_services)
                ? details.added_services
                : Array.isArray(item.added_services)
                  ? item.added_services
                  : [],
              type: item.type === "other" ? "service" : item.type,
            };
          } catch (err) {
            console.error("Error fetching appointment details:", err);
            return null;
          }
        }),
      );

      // Дедупликация по id — защита от дубликатов из API
      const seen = new Set();
      const filteredEvents = detailedEvents.filter((e) => {
        if (!e) return false;
        if (e.status?.toLowerCase() === "canceled") return false;
        if (e.isBreak && !e.name) return false;
        if (seen.has(e.id)) return false;
        seen.add(e.id);
        return true;
      });

      setDayEvents(filteredEvents);
      setDayAppointments(filteredEvents);
      setSelectedDate(day);
      setClickedDate(day);
      setShowModal(true);
    } catch (err) {
      console.error("❌ Day Fetch Error:", err.response?.data || err.message);
      toast.error(t("home.errorLoadAppointments"));
      if (err.response?.status === 401) {
        localStorage.clear();
        sessionStorage.clear();
        navigate("/sign-in");
      }
    }
  };

  useEffect(() => {
    if (token) {
      if (userCurrency) {
        setUserCurrencyCode(userCurrency);
        fetchMonthEvents();
      } else {
        fetchUserInfo().then(() => {
          fetchMonthEvents();
        });
      }
    }
  }, [currentDate, refreshKey, token, userCurrency]);

  useEffect(() => {
    if (userCurrency) {
      setUserCurrencyCode(userCurrency);
    }
  }, [userCurrency]);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const daysToRender = eachDayOfInterval({ start: startDate, end: endDate });

  return (
    <div className="relative">
      <motion.div
        key={currentDate}
        initial={{ x: direction === 1 ? 100 : -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: direction === 1 ? -100 : 100, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="grid grid-cols-7 min-w-[640px] md:min-w-0"
      >
        {daysToRender.map((day, idx) => {
          const isSelected = isSameDay(day, selectedDate);
          const inMonth = isSameMonth(day, currentDate);
          const dayKey = format(day, "yyyy-MM-dd");
          const event = monthEvents[dayKey];
          const hasGoogleEvent = googleEventDates.has(dayKey);
          const dayOfWeek = getDay(day);
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

          return (
            <div
              key={dayKey}
              onClick={() => fetchDayEvents(day)}
              className={clsx(
                "min-h-[64px] md:h-[110px] px-1 py-2 cursor-pointer relative flex flex-col items-center justify-between outline outline-[1px] outline-[#E5E7EB]",
                isSelected && "bg-[#1a2a8a] text-white",
                !isSelected && inMonth && "hover:bg-gray-100 text-gray-800",
                !inMonth && "text-gray-300",
                isWeekend &&
                  "bg-[repeating-linear-gradient(117deg,#f5f5f5_0_5px,transparent_5px_10px)]",
              )}
            >
              <span className="mt-1 text-[16px]">{format(day, "d")}</span>
              {event && (
                <div className="mt-2 flex flex-col items-center gap-1 w-full">
                  <div
                    className={clsx(
                      "flex items-center gap-2 rounded-full shadow-sm px-1 py-1 circle-in-calendar",
                      isSelected
                        ? "bg-white text-[#020637]"
                        : "bg-[#020637] text-white",
                    )}
                  >
                    {event.avatars?.[0] && (
                      <img
                        src={event.avatars[0]}
                        alt={t("home.avatarImageAlt")}
                        className="w-6 h-6 rounded-full"
                      />
                    )}
                    <span className="text-[12px] font-medium">
                      {event.label}
                    </span>
                  </div>
                  {event.extra && (
                    <div
                      className={clsx(
                        "rounded-full px-3 py-0.5 text-[11px] font-semibold transition-colors duration-150",
                        isSelected
                          ? "bg-white text-[#020637]"
                          : "bg-[#020637] text-white",
                      )}
                    >
                      {event.extra}
                    </div>
                  )}
                </div>
              )}

              {/* Зелёная точка — Google Calendar событие */}
              {hasGoogleEvent && (
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: isSelected ? "#86efac" : "#16a34a",
                    flexShrink: 0,
                    alignSelf: "center",
                    marginBottom: 2,
                  }}
                  title="Google Calendar"
                />
              )}
            </div>
          );
        })}
      </motion.div>

      <CalendarDayModal
        open={showModal}
        onClose={() => setShowModal(false)}
        appointments={dayAppointments}
        date={clickedDate}
        userStatus={userStatus}
        userCurrency={userCurrencyCode}
        isGoogleEventDay={
          !!(
            clickedDate &&
            googleEventDates.has(format(clickedDate, "yyyy-MM-dd"))
          )
        }
        onSaveSuccess={() => {
          fetchMonthEvents();
          if (clickedDate) {
            fetchDayEvents(clickedDate);
          }
        }}
      />
    </div>
  );
}
